import React from "react";
import { Loader2 } from "lucide-react";

// Single disciplined accent plus restrained health states.
const PILL_TONES = {
  on_track: "border-[rgba(34,197,94,0.35)] bg-[rgba(34,197,94,0.14)] text-[#D7F7E6]",
  at_risk: "border-[rgba(245,158,11,0.38)] bg-[rgba(245,158,11,0.16)] text-[#FFF1D6]",
  blocked: "border-[rgba(239,68,68,0.36)] bg-[rgba(239,68,68,0.14)] text-[#FFE0E0]",
  accent: "border-[rgba(43,79,216,0.40)] bg-[rgba(43,79,216,0.12)] text-[#DCE6FF]",
  neutral: "border-border bg-white/5 text-muted-foreground",
};

const DOT_TONES = {
  on_track: "bg-[#22C55E]",
  at_risk: "bg-[#F59E0B]",
  blocked: "bg-[#EF4444]",
  accent: "bg-[#2B4FD8]",
  neutral: "bg-white/40",
};

export const HEALTH_LABELS = { on_track: "On track", at_risk: "At risk", blocked: "Blocked" };

export const StatusPill = ({ tone = "neutral", label, dot = true, className = "", testId }) => (
  <span
    data-testid={testId || "status-pill"}
    className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[10.5px] uppercase tracking-[0.12em] font-medium ${PILL_TONES[tone] || PILL_TONES.neutral} ${className}`}
  >
    {dot && <span className={`h-1.5 w-1.5 rounded-full ${DOT_TONES[tone] || DOT_TONES.neutral}`} />}
    {label}
  </span>
);

export const Panel = ({ title, action, children, className = "", bodyClassName = "", testId }) => (
  <section
    data-testid={testId}
    className={`rounded-md border border-border bg-card shadow-[inset_0_1px_0_rgba(255,255,255,0.05)] ${className}`}
  >
    {(title || action) && (
      <header className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
        <h2 className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">{title}</h2>
        {action}
      </header>
    )}
    <div className={bodyClassName}>{children}</div>
  </section>
);

export const SectionLabel = ({ children, className = "" }) => (
  <span className={`text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium ${className}`}>
    {children}
  </span>
);

export const EmptyState = ({ message, className = "", testId }) => (
  <div
    data-testid={testId || "empty-state"}
    className={`flex items-center justify-center px-4 py-10 text-sm text-muted-foreground ${className}`}
  >
    {message}
  </div>
);

export const Loading = ({ label = "Loading", className = "" }) => (
  <div
    data-testid="loading-state"
    className={`flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground ${className}`}
  >
    <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
    {label}
  </div>
);

export const Kpi = ({ label, value, sub, testId }) => (
  <div className="flex flex-col gap-1">
    <SectionLabel>{label}</SectionLabel>
    <span data-testid={testId} className="font-mono tabular-nums text-foreground text-[15px] leading-none">
      {value}
    </span>
    {sub && <span className="font-mono tabular-nums text-[11px] text-muted-foreground">{sub}</span>}
  </div>
);
